/**
 * BLRRD Transform — public API.
 *
 * Phase 4.D.transform.1 (Wave 11.1) — Rotate / Scale / Flip / Free-transform.
 *
 * Architecture invariants (mirror tone-equalizer / capture-sharpen):
 *
 *   • Operates on Float32 RGBA buffers (scene-linear ACEScg in the
 *     editor pipeline; transforms are colour-space agnostic but the
 *     convention is linear so resampler interpolation is gamma-correct).
 *
 *   • Inverse-warp resampling: for each destination pixel we compute
 *     the source coordinate via the inverse 3×3 matrix and sample the
 *     source with the chosen kernel. This avoids forward-mapping holes
 *     and naturally produces the correct anti-aliasing for downscales
 *     (when paired with a sufficiently wide kernel — Lanczos-3 is the
 *     default for that reason).
 *
 *   • Identity short-circuit: a numerical identity matrix triggers a
 *     ref-passthrough of the source buffer (no copy, no resample).
 *     This is what keeps pixel-hash + worker-parity baselines byte-
 *     identical — transforms only ever apply work when the user
 *     explicitly invokes them.
 *
 *   • Resampler choice via `opts.resampler` — default `'lanczos3'`
 *     (Duchon 1979). Preview tier may pass `'bilinear'`.
 *
 *   • Edge handling delegated to the resampler — all three implement
 *     mirror-extend (Wolberg 1990 §5.3).
 *
 * References:
 *   • Hartley & Zisserman (2003), "Multiple View Geometry in Computer
 *     Vision", Cambridge UP — homogeneous coordinates + projective
 *     transforms (Ch. 2).
 *   • Wolberg (1990), "Digital Image Warping", IEEE — inverse-mapping
 *     resample pattern (§3.5), mirror-extend (§5.3).
 *   • Duchon (1979) — Lanczos kernel; see resample.js.
 *
 * License: MIT (BLRRD project).
 */

import {
    identity, multiply, invert, rotation, scale as scaleMatrix,
    translation, flipH as flipHMatrix, flipV as flipVMatrix,
    isIdentity, transformPoint, compose,
} from './matrix.js';
import { RESAMPLERS, getResampler } from './resample.js';

/** Number of channels per pixel — RGBA. */
const C = 4;

/**
 * Returns true when `m` is numerically the identity matrix.
 * Convenience re-export so callers don't need to also import from
 * `./matrix.js`.
 *
 * @param {Float32Array} m
 * @param {number} [eps=1e-6]
 * @returns {boolean}
 */
export function isIdentityMatrix(m, eps = 1e-6) {
    return isIdentity(m, eps);
}

/**
 * Apply a 3×3 transform matrix to a source Float32 RGBA buffer,
 * producing the destination buffer via inverse-warp resampling.
 *
 * @param {Float32Array} srcRgba  source pixels, length srcW*srcH*4
 * @param {number}       srcW
 * @param {number}       srcH
 * @param {Float32Array|null} dstRgba destination buffer; if null/undefined a
 *                       fresh `Float32Array(dstW*dstH*4)` is allocated
 * @param {number}       dstW
 * @param {number}       dstH
 * @param {Float32Array} matrix  3×3 forward source→dest matrix (row-major)
 * @param {object}       [opts]
 * @param {string}       [opts.resampler='lanczos3']  'bilinear' | 'bicubic' | 'lanczos3'
 * @param {number}       [opts.identityEps=1e-6]      identity short-circuit tolerance
 * @returns {Float32Array} the destination buffer
 *
 * Identity short-circuit: when `matrix` is identity AND src/dst dims
 * match AND `dstRgba` is null/undefined, returns the source reference.
 * Callers that need an isolated copy must clone after the call.
 */
export function applyTransform(srcRgba, srcW, srcH, dstRgba, dstW, dstH, matrix, opts) {
    if (!srcRgba || !(srcRgba instanceof Float32Array)) {
        throw new Error('applyTransform: srcRgba must be Float32Array');
    }
    if (!Number.isInteger(srcW) || !Number.isInteger(srcH) || srcW <= 0 || srcH <= 0) {
        throw new Error(`applyTransform: invalid src dims ${srcW}×${srcH}`);
    }
    if (!Number.isInteger(dstW) || !Number.isInteger(dstH) || dstW <= 0 || dstH <= 0) {
        throw new Error(`applyTransform: invalid dst dims ${dstW}×${dstH}`);
    }
    if (srcRgba.length !== srcW * srcH * C) {
        throw new Error(
            `applyTransform: src buffer length ${srcRgba.length} != ${srcW}×${srcH}×4`,
        );
    }
    if (!matrix || matrix.length !== 9) {
        throw new Error('applyTransform: matrix must be Float32Array(9)');
    }

    const o = opts || {};
    const resamplerId = o.resampler || 'lanczos3';
    const sampler = getResampler(resamplerId);
    const identityEps = (typeof o.identityEps === 'number') ? o.identityEps : 1e-6;

    /* Identity short-circuit. Requires same dims AND caller didn't pre-allocate
     * a dst — in that case the contract is "no copy, no work". */
    if (isIdentity(matrix, identityEps) && srcW === dstW && srcH === dstH && !dstRgba) {
        return srcRgba;
    }

    /* Allocate dst if needed. */
    const dst = dstRgba || new Float32Array(dstW * dstH * C);
    if (dst.length !== dstW * dstH * C) {
        throw new Error(
            `applyTransform: dst buffer length ${dst.length} != ${dstW}×${dstH}×4`,
        );
    }

    /* Inverse-warp: pre-compute matrix inverse. */
    const inv = invert(matrix);

    /* Hot loop. Inline `transformPoint` for one less function call per
     * pixel. For an affine matrix m6=m7=0 and m8=1 — we still do the
     * full projective divide here for correctness when the caller
     * supplies a perspective transform; the cost is negligible
     * compared to the resample. */
    const im0 = inv[0], im1 = inv[1], im2 = inv[2];
    const im3 = inv[3], im4 = inv[4], im5 = inv[5];
    const im6 = inv[6], im7 = inv[7], im8 = inv[8];

    for (let dy = 0; dy < dstH; dy++) {
        for (let dx = 0; dx < dstW; dx++) {
            const xp = im0 * dx + im1 * dy + im2;
            const yp = im3 * dx + im4 * dy + im5;
            const wp = im6 * dx + im7 * dy + im8;

            const dstOff = (dy * dstW + dx) * C;

            if (Math.abs(wp) < 1e-30 || !Number.isFinite(wp)) {
                /* Degenerate sample — write transparent black. */
                dst[dstOff] = 0; dst[dstOff + 1] = 0;
                dst[dstOff + 2] = 0; dst[dstOff + 3] = 0;
                continue;
            }
            const sx = xp / wp;
            const sy = yp / wp;

            sampler(srcRgba, srcW, srcH, sx, sy, dst, dstOff);
        }
    }

    return dst;
}

/**
 * Compute the axis-aligned bounding box of an image of size
 * `(w, h)` after transformation by `matrix`. Returns
 * `{ minX, minY, maxX, maxY, dstW, dstH, originShift }` where
 * `originShift = (-minX, -minY)` is the translation needed to
 * compose into `matrix` so the transformed image fits into a
 * canvas starting at (0,0).
 *
 * @param {number} w
 * @param {number} h
 * @param {Float32Array} matrix
 * @returns {{ minX:number, minY:number, maxX:number, maxY:number,
 *             dstW:number, dstH:number, originShift:[number,number] }}
 */
export function computeBounds(w, h, matrix) {
    const corners = [[0, 0], [w, 0], [w, h], [0, h]];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const tmp = new Float32Array(2);
    for (const [cx, cy] of corners) {
        transformPoint(matrix, cx, cy, tmp, 0);
        if (tmp[0] < minX) minX = tmp[0];
        if (tmp[1] < minY) minY = tmp[1];
        if (tmp[0] > maxX) maxX = tmp[0];
        if (tmp[1] > maxY) maxY = tmp[1];
    }
    const dstW = Math.max(1, Math.ceil(maxX - minX));
    const dstH = Math.max(1, Math.ceil(maxY - minY));
    return {
        minX, minY, maxX, maxY,
        dstW, dstH,
        originShift: [-minX, -minY],
    };
}

/**
 * Convenience: rotate around image center by `angleRad`.
 *
 * Returns `{ data, w, h }` — the destination buffer is freshly
 * allocated to fit the rotated bounds (the canvas grows for non-90°
 * rotations).
 *
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @param {number} angleRad
 * @param {object} [opts]
 * @returns {{ data: Float32Array, w: number, h: number, matrix: Float32Array }}
 */
export function rotate(rgba, w, h, angleRad, opts) {
    /* Compose: translate-by-shift · rotation-around-center.
     *   M = T(-minX, -minY) · T(cx,cy) · R(θ) · T(-cx,-cy)
     */
    const cx = w * 0.5;
    const cy = h * 0.5;
    const baseM = compose(
        translation(cx, cy),
        rotation(angleRad),
        translation(-cx, -cy),
    );
    const bounds = computeBounds(w, h, baseM);
    const fullM  = compose(
        translation(bounds.originShift[0], bounds.originShift[1]),
        baseM,
    );
    const data = applyTransform(
        rgba, w, h, null, bounds.dstW, bounds.dstH, fullM, opts,
    );
    return { data, w: bounds.dstW, h: bounds.dstH, matrix: fullM };
}

/**
 * Convenience: scale image by `(sx, sy)` anchored at the origin.
 * For uniform scale pass the same value twice.
 *
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @param {number} sx
 * @param {number} sy
 * @param {object} [opts]
 * @returns {{ data: Float32Array, w: number, h: number, matrix: Float32Array }}
 */
export function scale(rgba, w, h, sx, sy, opts) {
    if (!Number.isFinite(sx) || !Number.isFinite(sy) || sx === 0 || sy === 0) {
        throw new Error(`scale: invalid factors sx=${sx} sy=${sy}`);
    }
    const M = scaleMatrix(sx, sy);
    const bounds = computeBounds(w, h, M);
    const fullM = compose(
        translation(bounds.originShift[0], bounds.originShift[1]),
        M,
    );
    const data = applyTransform(
        rgba, w, h, null, bounds.dstW, bounds.dstH, fullM, opts,
    );
    return { data, w: bounds.dstW, h: bounds.dstH, matrix: fullM };
}

/**
 * Convenience: horizontal flip (mirror around vertical centerline).
 * Does NOT need a resampler — produces byte-identical output via
 * index swap.
 *
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @returns {{ data: Float32Array, w: number, h: number }}
 */
export function flipHorizontal(rgba, w, h) {
    if (!(rgba instanceof Float32Array) || rgba.length !== w * h * C) {
        throw new Error('flipHorizontal: invalid buffer');
    }
    const dst = new Float32Array(rgba.length);
    for (let y = 0; y < h; y++) {
        const rowOff = y * w * C;
        for (let x = 0; x < w; x++) {
            const sOff = rowOff + x * C;
            const dOff = rowOff + (w - 1 - x) * C;
            dst[dOff]     = rgba[sOff];
            dst[dOff + 1] = rgba[sOff + 1];
            dst[dOff + 2] = rgba[sOff + 2];
            dst[dOff + 3] = rgba[sOff + 3];
        }
    }
    return { data: dst, w, h };
}

/**
 * Convenience: vertical flip (mirror around horizontal centerline).
 *
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @returns {{ data: Float32Array, w: number, h: number }}
 */
export function flipVertical(rgba, w, h) {
    if (!(rgba instanceof Float32Array) || rgba.length !== w * h * C) {
        throw new Error('flipVertical: invalid buffer');
    }
    const dst = new Float32Array(rgba.length);
    const rowBytes = w * C;
    for (let y = 0; y < h; y++) {
        const sOff = y * rowBytes;
        const dOff = (h - 1 - y) * rowBytes;
        dst.set(rgba.subarray(sOff, sOff + rowBytes), dOff);
    }
    return { data: dst, w, h };
}

/**
 * Convenience: rotate by an exact multiple of 90° using direct index
 * swap — lossless, no resampling. `turns` is the number of 90° CW
 * turns (negative for CCW). Modulo 4.
 *
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @param {number} turns
 * @returns {{ data: Float32Array, w: number, h: number }}
 */
export function rotate90(rgba, w, h, turns) {
    let n = ((turns % 4) + 4) % 4;
    if (n === 0) {
        return { data: new Float32Array(rgba), w, h };
    }
    if (n === 2) {
        const dst = new Float32Array(rgba.length);
        const total = w * h;
        for (let i = 0; i < total; i++) {
            const sOff = i * C;
            const dOff = (total - 1 - i) * C;
            dst[dOff]     = rgba[sOff];
            dst[dOff + 1] = rgba[sOff + 1];
            dst[dOff + 2] = rgba[sOff + 2];
            dst[dOff + 3] = rgba[sOff + 3];
        }
        return { data: dst, w, h };
    }
    /* n === 1 (90 CW) or n === 3 (90 CCW). */
    const dW = h, dH = w;
    const dst = new Float32Array(dW * dH * C);
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const sOff = (y * w + x) * C;
            let dx, dy;
            if (n === 1) {           /* 90° CW: (x,y) -> (h-1-y, x) */
                dx = h - 1 - y;
                dy = x;
            } else {                  /* 90° CCW: (x,y) -> (y, w-1-x) */
                dx = y;
                dy = w - 1 - x;
            }
            const dOff = (dy * dW + dx) * C;
            dst[dOff]     = rgba[sOff];
            dst[dOff + 1] = rgba[sOff + 1];
            dst[dOff + 2] = rgba[sOff + 2];
            dst[dOff + 3] = rgba[sOff + 3];
        }
    }
    return { data: dst, w: dW, h: dH };
}

/* Re-exports — convenience for consumers importing the namespace
 * once and not wanting to dig into the sub-modules. NOTE: matrix.js
 * exports `scale`, `flipH`, `flipV` which collide with this file's
 * higher-level convenience functions. Consumers that want the raw
 * matrix builders should import from `./matrix.js` directly or use
 * the `BlrrdTransform.Matrix.scale` namespace surface. */
export {
    identity, multiply, invert, rotation, translation,
    isIdentity, transformPoint, compose, determinant,
} from './matrix.js';
export { RESAMPLERS, getResampler } from './resample.js';
